import React, { useState, useEffect } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import axios from "axios";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { Card, Spinner } from "react-bootstrap";
import { vendasBreadcrumbItems, VendaType } from "data/venda";
import VendaTable, { vendaTableColumns } from "components/tables/VendaTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { useEmpresas } from "providers/EmpresaProvider";
import qs from "qs";
import { useSearchParams } from "react-router-dom";

const Vendas = () => {
  const { selectedIds } = useEmpresas();
  const [searchParams] = useSearchParams();
  const vendaId = searchParams.get('vendaId');

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [tabFilter, setTabFilter] = useState<string | undefined>();

  // Efeito para resetar paginação quando há vendaId na URL
  useEffect(() => {
    if (vendaId && pagination.pageIndex !== 0) {
      setPagination(prev => ({
        ...prev,
        pageIndex: 0
      }));
    }
  }, [vendaId, pagination.pageIndex]);

  // Consulta para obter as vendas
  const vendasQuery = useQuery({
    queryKey: [
      "vendas",
      pagination.pageIndex + 1,
      pagination.pageSize,
      tabFilter,
      selectedIds,
      vendaId, // Incluir vendaId na chave para forçar nova consulta quando mudar
    ],
    queryFn: async () => {
      const params: any = {
        pageIndex: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        status: tabFilter,
        empresasId: selectedIds,
      };

      // Se há vendaId específico, usar como termo de busca para garantir que apareça nos resultados
      if (vendaId) {
        params.termoBusca = vendaId;
      }

      const { data } = await axios.get("/api/venda", {
        params,
        paramsSerializer: (params) => qs.stringify(params, { indices: false }),
      });
      return data;
    },
    placeholderData: keepPreviousData,
  });

  // Preparar dados para a tabela
  const tableData = React.useMemo(() => {
    if (!vendasQuery.data) return [];

    return vendasQuery.data.data.map((item: any) => ({
      ...item,
    })) as VendaType[];
  }, [vendasQuery.data]);

  // Configuração da tabela usando useReactTable
  const table = useReactTable({
    data: tableData,
    columns: vendaTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: vendasQuery.data
      ? Math.ceil(vendasQuery.data.total / pagination.pageSize)
      : 1,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    // Habilitar a expansão de linhas
    getRowCanExpand: () => true,
  });

  if (vendasQuery.isLoading) {
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Carregando...</span>
      </Spinner>
    );
  }

  return (
    <div>
      <PageBreadcrumb items={vendasBreadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Vendas</h2>
        </div>

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                <VendaTable />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default Vendas;
